import React, { useState, useRef, useEffect } from 'react';
import '../styles/DatePicker.css';

interface DatePickerProps {
  value: string;
  onChange: (date: string) => void;
  placeholder?: string;
  minDate?: string; // YYYY-MM-DD format
  className?: string;
}

const DatePicker: React.FC<DatePickerProps> = ({ 
  value, 
  onChange, 
  placeholder = "Select date",
  minDate,
  className = ""
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState<Date | null>(value ? new Date(value) : null);
  const datePickerRef = useRef<HTMLDivElement>(null);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Initialize with current date if no minDate specified
  useEffect(() => {
    if (!minDate) {
      setCurrentMonth(new Date());
    } else {
      const minDateObj = new Date(minDate);
      setCurrentMonth(minDateObj);
    }
  }, [minDate]);

  const getDaysInMonth = (date: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfMonth = new Date(year, month, 1).getDay();
    
    const days = [];
    
    // Add empty cells for days before the first day of the month
    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(null);
    }
    
    // Add days of the month
    for (let i = 1; i <= daysInMonth; i++) {
      days.push(new Date(year, month, i));
    }
    
    return days;
  };

  const isDateDisabled = (date: Date) => {
    if (minDate) {
      const minDateObj = new Date(minDate);
      return date < minDateObj;
    }
    return false;
  };

  const isDateSelected = (date: Date) => {
    if (!selectedDate) return false;
    return date.toDateString() === selectedDate.toDateString();
  };

  const isToday = (date: Date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const handleDateSelect = (date: Date) => {
    if (isDateDisabled(date)) return;
    
    setSelectedDate(date);
    const formattedDate = date.toISOString().split('T')[0]; // YYYY-MM-DD format
    onChange(formattedDate);
    setIsOpen(false);
  };

  const goToPreviousMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1, 1));
  };

  const goToNextMonth = () => {
    setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1, 1));
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long' 
    });
  };

  const formatDisplayValue = (dateString: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const days = getDaysInMonth(currentMonth);
  const weekDays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div className={`date-picker-container ${className}`} ref={datePickerRef}>
      <input
        type="text"
        value={formatDisplayValue(value)}
        onClick={() => setIsOpen(!isOpen)}
        placeholder={placeholder}
        readOnly
        className="date-picker-input"
      />
      
      {isOpen && (
        <div className="date-picker-dropdown">
          {/* Header */}
          <div className="date-picker-header">
            <button 
              type="button" 
              onClick={goToPreviousMonth}
              className="date-picker-nav-btn"
              disabled={!!minDate && currentMonth <= new Date(minDate)}
            >
              ‹
            </button>
            <span className="date-picker-month-year">
              {formatDate(currentMonth)}
            </span>
            <button 
              type="button" 
              onClick={goToNextMonth}
              className="date-picker-nav-btn"
            >
              ›
            </button>
          </div>

          {/* Week days */}
          <div className="date-picker-weekdays">
            {weekDays.map(day => (
              <div key={day} className="date-picker-weekday">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="date-picker-grid">
            {days.map((day, index) => (
              <div key={index} className="date-picker-day-wrapper">
                {day ? (
                  <button
                    type="button"
                    onClick={() => handleDateSelect(day)}
                    className={`date-picker-day ${
                      isDateSelected(day) ? 'selected' : ''
                    } ${
                      isToday(day) ? 'today' : ''
                    } ${
                      isDateDisabled(day) ? 'disabled' : ''
                    }`}
                    disabled={isDateDisabled(day)}
                  >
                    {day.getDate()}
                  </button>
                ) : (
                  <div className="date-picker-day empty"></div>
                )}
              </div>
            ))}
          </div>

          {/* Today button */}
          <div className="date-picker-footer">
            <button
              type="button"
              onClick={() => {
                const today = new Date();
                if (!isDateDisabled(today)) {
                  handleDateSelect(today);
                }
              }}
              className="date-picker-today-btn"
              disabled={!!minDate && new Date() < new Date(minDate)}
            >
              Today
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DatePicker;
